(() => {
  'use strict';
  const instances = new WeakMap();
  function createViewer(dialog, picture, toggle, listen, strings) {
    const surface = dialog.querySelector('.gallery-viewer-stage');
    let scale = 1, x = 0, y = 0, width = 0, height = 0, maxScale = 1;
    let viewportWidth = 0, viewportHeight = 0, gesture = null, pinch = null, ignoreClickUntil = 0;
    const pointers = new Map();
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    function paint() {
      const limitX = Math.max(0, (width * scale - viewportWidth) / 2);
      const limitY = Math.max(0, (height * scale - viewportHeight) / 2);
      x = clamp(x, -limitX, limitX); y = clamp(y, -limitY, limitY);
      if (scale <= 1.01) {scale = 1; x = y = 0;}
      picture.style.width = `${width}px`; picture.style.height = `${height}px`;
      picture.style.transform = `translate(-50%, -50%) translate3d(${x}px, ${y}px, 0) scale(${scale})`;
      dialog.dataset.zoomed = String(scale > 1);
      dialog.dataset.viewerScale = scale.toFixed(3);
      toggle.disabled = maxScale <= 1.01;
      toggle.setAttribute('aria-pressed', String(scale > 1));
      toggle.setAttribute('aria-label', scale > 1 ? strings.zoomOut : strings.zoomIn);
      toggle.title = scale > 1 ? strings.zoomOut : strings.zoomIn;
    }
    function measure() {
      if (!dialog.open || !picture.naturalWidth || !picture.naturalHeight) return;
      viewportWidth = surface.clientWidth; viewportHeight = surface.clientHeight;
      const gutter = viewportWidth > 900 ? 144 : 32;
      const fit = Math.min(1, Math.max(1, viewportWidth - gutter) / picture.naturalWidth, Math.max(1, viewportHeight - 48) / picture.naturalHeight);
      width = picture.naturalWidth * fit; height = picture.naturalHeight * fit;
      maxScale = Math.max(1, Math.min(4, 1 / fit));
      scale = clamp(scale, 1, maxScale); paint();
    }
    function reset() {
      scale = 1; x = y = 0;
      const captured = [...pointers.keys()];
      if (captured.length) ignoreClickUntil = performance.now() + 400;
      pointers.clear(); gesture = pinch = null;
      captured.forEach(id => {if (surface.hasPointerCapture(id)) surface.releasePointerCapture(id);});
      surface.removeAttribute('data-panning');
      measure();
    }
    const point = (clientX, clientY) => {
      const rect = surface.getBoundingClientRect();
      return {x: clientX - rect.left - viewportWidth / 2, y: clientY - rect.top - viewportHeight / 2};
    };
    function zoomTo(value, focus = {x: 0, y: 0}) {
      const next = clamp(value, 1, maxScale), ratio = next / scale;
      x = focus.x - (focus.x - x) * ratio; y = focus.y - (focus.y - y) * ratio;
      scale = next; paint();
    }
    function toggleZoom(focus) {zoomTo(scale > 1 ? 1 : Math.min(2.5, maxScale), focus);}
    function startPinch() {
      const [a, b] = [...pointers.values()];
      const center = point((a.x + b.x) / 2, (a.y + b.y) / 2);
      pinch = {distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), center, scale, x, y};
      if (gesture) gesture.moved = true;
    }
    listen(picture, 'load', measure);
    listen(window, 'resize', measure);
    listen(toggle, 'click', () => toggleZoom());
    listen(surface, 'dragstart', event => event.preventDefault());
    listen(surface, 'pointerdown', event => {
      if (event.button !== 0 || !dialog.open) return;
      ignoreClickUntil = 0;
      pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
      surface.setPointerCapture(event.pointerId);
      if (pointers.size === 1) gesture = {x: event.clientX, y: event.clientY, offsetX: x, offsetY: y, moved: false};
      else if (pointers.size === 2) startPinch();
    });
    listen(surface, 'pointermove', event => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
      if (pointers.size >= 2 && pinch) {
        const [a, b] = [...pointers.values()], center = point((a.x + b.x) / 2, (a.y + b.y) / 2);
        scale = clamp(pinch.scale * Math.hypot(a.x - b.x, a.y - b.y) / pinch.distance, 1, maxScale);
        x = center.x - (pinch.center.x - pinch.x) * scale / pinch.scale;
        y = center.y - (pinch.center.y - pinch.y) * scale / pinch.scale;
        surface.dataset.panning = 'true'; paint(); return;
      }
      if (!gesture) return;
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (Math.hypot(dx, dy) > 5) gesture.moved = true;
      if (scale > 1 && gesture.moved) {
        x = gesture.offsetX + dx; y = gesture.offsetY + dy;
        surface.dataset.panning = 'true'; paint();
      }
    });
    function finishPointer(event, cancelled = false) {
      if (!pointers.has(event.pointerId)) return;
      if (cancelled || gesture?.moved || pinch) ignoreClickUntil = performance.now() + 400;
      pointers.delete(event.pointerId);
      if (surface.hasPointerCapture(event.pointerId)) surface.releasePointerCapture(event.pointerId);
      pinch = null;
      if (pointers.size) {
        const remaining = [...pointers.values()][0];
        gesture = {x: remaining.x, y: remaining.y, offsetX: x, offsetY: y, moved: true};
      } else {gesture = null; surface.removeAttribute('data-panning');}
    }
    listen(surface, 'pointerup', event => finishPointer(event));
    listen(surface, 'pointercancel', event => finishPointer(event, true));
    listen(surface, 'lostpointercapture', event => finishPointer(event, true));
    listen(surface, 'click', event => {
      if (performance.now() < ignoreClickUntil) {event.preventDefault(); return;}
      const bounds = picture.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {dialog.close(); return;}
      toggleZoom(point(event.clientX, event.clientY));
    });
    listen(surface, 'wheel', event => {
      if (event.ctrlKey) return;
      event.preventDefault();
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewportHeight : 1);
      zoomTo(scale * Math.exp(-clamp(delta, -120, 120) * .004), point(event.clientX, event.clientY));
    }, {passive: false});
    return {
      reset,
      key(event) {
        if (event.key === '+' || event.key === '=') {zoomTo(scale * 1.5); return true;}
        if (event.key === '-') {zoomTo(scale / 1.5); return true;}
        if (event.key === '0') {zoomTo(1); return true;}
        return false;
      }
    };
  }
  function createThumbnailRail(root, thumbs, listen, reducedMotion) {
    const strip = root.querySelector('[data-gallery-strip]');
    const rail = root.querySelector('[data-gallery-thumbs]');
    if (!strip || !rail || thumbs.length < 2) return {reveal() {}, destroy() {}};
    const controls = [...root.querySelectorAll('[data-thumbs-step]')];
    let selected = thumbs.findIndex(el => el.dataset.selected === 'true');
    let resizeFrame = 0;
    const updateControls = () => {
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      controls.forEach(button => {button.disabled = Number(button.dataset.thumbsStep) < 0 ? rail.scrollLeft <= 1 : rail.scrollLeft >= maximum - 1;});
    };
    const reveal = (index, animate = true) => {
      selected = index;
      const thumb = thumbs[index];
      if (!thumb) return;
      const bounds = rail.getBoundingClientRect(), item = thumb.getBoundingClientRect();
      const outside = item.left < bounds.left - 1 || item.right > bounds.right + 1;
      if (outside) rail.scrollTo({left: rail.scrollLeft + item.left - bounds.left - (rail.clientWidth - item.width) / 2, behavior: animate && !reducedMotion() ? 'smooth' : 'instant'});
      updateControls();
    };
    const measure = () => {
      resizeFrame = 0;
      const span = thumbs.at(-1).getBoundingClientRect().right - thumbs[0].getBoundingClientRect().left;
      const overflow = span > strip.clientWidth + 1;
      strip.dataset.overflow = String(overflow);
      controls.forEach(button => {button.hidden = !overflow;});
      reveal(selected, false);
    };
    controls.forEach(button => listen(button, 'click', () => {
      const step = Number(button.dataset.thumbsStep);
      const thumb = thumbs[0].getBoundingClientRect(), gap = Number.parseFloat(getComputedStyle(rail).columnGap) || 0;
      const distance = Math.max(thumb.width + gap, Math.floor(rail.clientWidth / (thumb.width + gap)) * (thumb.width + gap));
      rail.scrollTo({left: rail.scrollLeft + step * distance, behavior: reducedMotion() ? 'instant' : 'smooth'});
    }));
    thumbs.forEach((thumb, index) => listen(thumb, 'focus', () => {if (thumb.matches(':focus-visible')) reveal(index, false);}));
    listen(rail, 'scroll', updateControls, {passive: true});
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(measure);
    });
    observer.observe(strip);
    measure();
    return {reveal, destroy() {observer.disconnect(); cancelAnimationFrame(resizeFrame);}};
  }
  function createMobileRail(root, stage, image, thumbs, initial, listen, reducedMotion, changed) {
    if (thumbs.length < 2) return {active: () => false, remember() {}, align() {}, destroy() {}};
    const mobile = matchMedia('(max-width: 900px)');
    let rail, pictures = [], selected = initial, frame = 0, settleTimer = 0, width = 0, touching = false;
    const active = () => mobile.matches;
    const railWidth = () => rail.getBoundingClientRect().width;
    const settle = () => {
      if (!active() || touching || !railWidth()) return;
      const destination = Math.round(rail.scrollLeft / railWidth()) * railWidth();
      // Some interrupted touch scrolls end between snap points without a final snap.
      if (Math.abs(rail.scrollLeft - destination) > .5) rail.scrollTo({left: destination, behavior: reducedMotion() ? 'instant' : 'smooth'});
    };
    const remember = index => {
      selected = index;
      pictures.forEach((picture, i) => picture.parentElement.setAttribute('aria-hidden', String(i !== index)));
    };
    const build = () => {
      if (rail) return;
      rail = document.createElement('div');
      rail.className = 'gallery-mobile-rail';
      pictures = thumbs.map((thumb, index) => {
        const slide = document.createElement('div');
        slide.className = 'gallery-mobile-slide';
        const picture = index === initial ? image.cloneNode(false) : document.createElement('img');
        picture.removeAttribute('data-gallery-image');
        picture.removeAttribute('id');
        picture.className = 'gallery-mobile-image';
        picture.alt = thumb.dataset.galleryAlt;
        picture.decoding = 'async';
        picture.loading = Math.abs(index - initial) <= 1 ? 'eager' : 'lazy';
        if (index !== initial) {
          picture.sizes = `${Math.ceil(stage.clientWidth)}px`;
          picture.srcset = thumb.dataset.gallerySrcset || '';
          picture.src = thumb.dataset.galleryPreview || thumb.dataset.gallerySrc;
        }
        slide.append(picture); rail.append(slide);
        return picture;
      });
      stage.append(rail);
      remember(selected);
      // The browser owns touch tracking, inertia and snapping, including Safari cancellation.
      listen(rail, 'scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          frame = 0;
          if (!active() || !railWidth()) return;
          const index = Math.max(0, Math.min(thumbs.length - 1, Math.round(rail.scrollLeft / railWidth())));
          if (index !== selected) {remember(index); changed(index);}
        });
        clearTimeout(settleTimer);
        settleTimer = setTimeout(settle, 160);
      }, {passive: true});
      listen(rail, 'scrollend', settle, {passive: true});
      listen(rail, 'touchstart', () => {touching = true; clearTimeout(settleTimer);}, {passive: true});
      const endTouch = event => {
        touching = event.touches.length > 0;
        clearTimeout(settleTimer); settleTimer = setTimeout(settle, 160);
      };
      listen(rail, 'touchend', endTouch, {passive: true});
      listen(rail, 'touchcancel', endTouch, {passive: true});
    };
    const align = () => {
      if (!active()) return;
      build();
      width = railWidth();
      pictures.forEach(picture => {picture.sizes = `${Math.ceil(width)}px`;});
      rail.scrollTo({left: selected * width, behavior: 'instant'});
    };
    const resize = () => {
      const enabled = active();
      root.classList.toggle('gallery-mobile', enabled);
      if (enabled) {
        build();
        if (railWidth() !== width) align();
      }
    };
    listen(mobile, 'change', () => {resize(); align();});
    const observer = new ResizeObserver(resize);
    observer.observe(stage);
    resize(); align();
    return {
      active,
      remember,
      align,
      go(index) {
        build(); remember(index);
        rail.scrollTo({left: index * railWidth(), behavior: reducedMotion() ? 'instant' : 'smooth'});
        changed(index);
      },
      picture(index, retry = false) {
        build();
        if (retry) {
          const replacement = pictures[index].cloneNode(false);
          pictures[index].replaceWith(replacement);
          pictures[index] = replacement;
        }
        return pictures[index];
      },
      destroy() {observer.disconnect(); cancelAnimationFrame(frame); clearTimeout(settleTimer); rail?.remove(); root.classList.remove('gallery-mobile');}
    };
  }
  function mount(root) {
    if (instances.has(root)) return;
    const $ = selector => root.querySelector(selector);
    const $$ = selector => [...root.querySelectorAll(selector)];
    const image = $('[data-gallery-image]');
    const thumbs = $$('[data-gallery-src]');
    const stage = $('.gallery-stage');
    const previewSizes = () => `${Math.ceil(stage.clientWidth)}px`;
    const dialog = $('[data-gallery-dialog]');
    const trigger = $('[data-gallery-zoom]');
    const large = $('[data-zoom-image]');
    if (!image || !thumbs.length || !dialog || !trigger || !large) return;
    const strings = JSON.parse($('[data-gallery-translations]').textContent);
    const events = new AbortController();
    const listen = (el, type, fn, options = {}) => el.addEventListener(type, fn, {...options, signal: events.signal});
    const viewer = createViewer(dialog, large, $('[data-gallery-magnify]'), listen, strings);
    let current = Math.max(0, thumbs.findIndex(el => el.dataset.selected === 'true'));
    let wanted = current, sequence = 0, cancelLoad = () => {}, returnFocus = trigger;
    let warmTimer = 0;
    const previews = new Map();
    const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
    const thumbnailRail = createThumbnailRail(root, thumbs, listen, reducedMotion);
    const mobileRail = createMobileRail(root, stage, image, thumbs, current, listen, reducedMotion, index => {
      cancel();
      commit(index, thumbs[index].dataset.gallerySrc, false);
      watchMobileImage(index);
    });
    const previewFor = (index, sizes = previewSizes()) => {
      const key = `${index}:${sizes}:${window.devicePixelRatio}`;
      if (previews.has(key)) return previews.get(key);
      const preload = new Image();
      preload.decoding = 'async';
      preload.sizes = sizes;
      preload.srcset = thumbs[index].dataset.gallerySrcset || '';
      preload.src = thumbs[index].dataset.galleryPreview || thumbs[index].dataset.gallerySrc;
      previews.set(key, preload);
      if (previews.size > 5) previews.delete(previews.keys().next().value);
      return preload;
    };
    const warmNeighbors = () => {
      clearTimeout(warmTimer);
      if (thumbs.length < 2 || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      warmTimer = setTimeout(() => {
        [1, -1].forEach(step => previewFor((current + step + thumbs.length) % thumbs.length, dialog.open ? '2000px' : previewSizes()));
      }, 160);
    };
    const countText = index => strings.count.replace('__current__', index + 1).replace('__total__', thumbs.length);
    const feedback = (text = '', error = false) => {
      $$('[data-gallery-status]').forEach(el => {el.textContent = text; el.dataset.error = String(error);});
      $$('[data-gallery-retry]').forEach(el => {el.hidden = !error;});
    };
    const busy = value => {
      $$('[data-gallery-stage]').forEach(el => el.setAttribute('aria-busy', String(value)));
      thumbs.forEach((el, i) => {el.dataset.pending = String(value && i === wanted);});
    };
    const cancel = () => {sequence++; cancelLoad(); cancelLoad = () => {}; busy(false);};
    function commit(index, src, animate = true) {
      const changed = current !== index;
      current = wanted = index;
      mobileRail.remember(index);
      const alt = thumbs[index].dataset.galleryAlt;
      const thumb = thumbs[index], original = thumb.dataset.gallerySrc;
      image.sizes = previewSizes();
      image.srcset = thumb.dataset.gallerySrcset || '';
      image.src = thumb.dataset.galleryPreview || src;
      image.alt = alt;
      trigger.href = original;
      if (dialog.open) {if (changed) viewer.reset(); large.src = src; large.alt = alt;}
      thumbs.forEach((el, i) => {
        el.dataset.selected = String(i === index);
        el.setAttribute('aria-pressed', String(i === index));
      });
      thumbnailRail.reveal(index, !dialog.open);
      $$('[data-gallery-caption]').forEach(el => {el.textContent = alt;});
      $$('[data-gallery-count]').forEach(el => {el.textContent = countText(index);});
      busy(false);
      feedback();
      warmNeighbors();
      if (changed && animate && !reducedMotion() && !navigator.connection?.saveData) {
        const target = dialog.open ? large : image;
        target.getAnimations().forEach(animation => animation.cancel());
        target.animate([{opacity: .85}, {opacity: 1}], {duration: 140, easing: 'ease-out'});
      }
    }
    function watchMobileImage(index, retry = false) {
      const picture = mobileRail.picture(index, retry), token = sequence;
      const cleanup = () => {
        clearTimeout(loadingTimer); clearTimeout(timeout);
        picture.removeEventListener('load', loaded); picture.removeEventListener('error', failed);
      };
      const loaded = () => {
        if (token !== sequence) return;
        cleanup(); busy(false); feedback(); cancelLoad = () => {};
      };
      const failed = () => {
        if (token !== sequence) return;
        cleanup(); busy(false); feedback(strings.error, true); cancelLoad = () => {};
      };
      const loadingTimer = setTimeout(() => {if (token === sequence) feedback(strings.loading);}, 180);
      const timeout = setTimeout(failed, 12000);
      cancelLoad = cleanup;
      busy(true);
      picture.addEventListener('load', loaded); picture.addEventListener('error', failed);
      if (picture.complete) {if (picture.naturalWidth) loaded(); else failed();}
    }
    function select(index, force = false) {
      if (mobileRail.active() && !dialog.open) {
        const target = (index + thumbs.length) % thumbs.length;
        mobileRail.go(target);
        if (force) {cancel(); watchMobileImage(target, true);}
        return;
      }
      cancel();
      wanted = (index + thumbs.length) % thumbs.length;
      if (wanted === current && image.complete && image.naturalWidth && !force && !dialog.open) {
        feedback();
        return;
      }
      const token = sequence, target = wanted;
      const preload = previewFor(target, dialog.open ? '2000px' : previewSizes());
      busy(true);
      feedback();
      const loadingTimer = setTimeout(() => feedback(strings.loading), 180);
      let decoding = false;
      const cleanup = () => {
        clearTimeout(timer); clearTimeout(loadingTimer);
        preload.removeEventListener('load', loaded);
        preload.removeEventListener('error', fail);
      };
      const fail = () => {
        if (token !== sequence) return;
        cleanup(); sequence++; cancelLoad = () => {}; busy(false); feedback(strings.error, true);
        for (const [key, value] of previews) if (value === preload) previews.delete(key);
      };
      const loaded = async () => {
        if (decoding) return;
        decoding = true;
        try {await preload.decode();} catch {}
        if (token !== sequence) return;
        if (!preload.naturalWidth) {fail(); return;}
        cleanup(); cancelLoad = () => {};
        commit(target, preload.currentSrc || preload.src);
      };
      const timer = setTimeout(fail, 12000);
      cancelLoad = cleanup;
      preload.addEventListener('load', loaded);
      preload.addEventListener('error', fail);
      if (preload.complete) {
        if (preload.naturalWidth) loaded();
        else fail();
      }
    }

    function keyboardIndex(key) {
      if (key === 'ArrowRight') return wanted + 1;
      if (key === 'ArrowLeft') return wanted - 1;
      if (key === 'Home') return 0;
      if (key === 'End') return thumbs.length - 1;
      return null;
    }
    thumbs.forEach((el, i) => {
      el.setAttribute('role', 'button');
      el.setAttribute('aria-pressed', String(i === current));
      listen(el, 'click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); select(i);
      });
      listen(el, 'keydown', event => {
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        if (event.key === ' ') {event.preventDefault(); select(i); return;}
        const index = keyboardIndex(event.key);
        if (index === null) return;
        event.preventDefault(); select(index); thumbs[wanted].focus({preventScroll: true});
      });
    });
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', dialog.id);
    listen(trigger, 'keydown', event => {if (event.key === ' ') {event.preventDefault(); trigger.click();}});
    listen(trigger, 'click', event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (dialog.open) return;
      document.querySelectorAll('dialog[open]').forEach(el => el.close());
      returnFocus = trigger;
      large.src = image.currentSrc || image.src;
      large.alt = image.alt;
      dialog.showModal();
      viewer.reset();
      $('[data-gallery-close]').focus({preventScroll: true});
      select(wanted, true);
    });
    listen($('[data-gallery-close]'), 'click', () => dialog.close());
    listen(dialog, 'close', () => {
      cancel(); wanted = current; feedback(); viewer.reset();
      mobileRail.align();
      if (returnFocus.isConnected) returnFocus.focus({preventScroll: true});
    });
    listen(dialog, 'keydown', event => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (viewer.key(event)) {event.preventDefault(); return;}
      const index = keyboardIndex(event.key);
      if (index !== null) {event.preventDefault(); select(index); return;}
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll('a[href],button')].filter(el => !el.disabled && !el.hidden && el.getClientRects().length);
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {event.preventDefault(); last.focus();}
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) {event.preventDefault(); first.focus();}
    });
    $$('[data-gallery-step]').forEach(el => listen(el, 'click', () => select(wanted + Number(el.dataset.galleryStep))));
    $$('.gallery-stage-nav [data-gallery-step]').forEach(el => listen(el, 'keydown', event => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const index = keyboardIndex(event.key);
      if (index !== null) {event.preventDefault(); select(index);}
    }));
    if (thumbs.length > 1) [stage].forEach(surface => {
      const picture = surface.querySelector('img');
      let gesture = null, suppressClickUntil = 0;
      const reset = (animate = true) => {
        const from = picture.style.translate;
        picture.style.removeProperty('translate');
        surface.removeAttribute('data-dragging');
        if (from && animate && !reducedMotion()) picture.animate([{translate: from}, {translate: '0px 0px'}], {duration: 160, easing: 'ease-out'});
        gesture = null;
      };
      listen(surface, 'dragstart', event => event.preventDefault());
      listen(surface, 'pointerdown', event => {
        if (mobileRail.active()) return;
        suppressClickUntil = 0;
        if (!event.isPrimary) {reset(); return;}
        if (event.button !== 0 || event.target.closest('a,button')) return;
        picture.getAnimations().forEach(animation => animation.cancel());
        gesture = {id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), horizontal: false};
      });
      listen(surface, 'pointermove', event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
        if (!gesture.horizontal) {
          if (Math.abs(dy) > 8 && Math.abs(dy) >= Math.abs(dx)) {reset(false); return;}
          if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
          gesture.horizontal = true;
          surface.setPointerCapture(event.pointerId);
          surface.dataset.dragging = 'true';
        }
        const limit = surface.clientWidth * .2;
        if (!reducedMotion()) picture.style.translate = `${Math.max(-limit, Math.min(limit, dx * .45))}px 0px`;
      });
      listen(surface, 'pointerup', event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const dx = event.clientX - gesture.x, elapsed = performance.now() - gesture.time;
        const horizontal = gesture.horizontal;
        const threshold = Math.max(36, Math.min(80, surface.clientWidth * .12));
        const advance = horizontal && (Math.abs(dx) >= threshold || (Math.abs(dx) >= 20 && elapsed < 280));
        if (horizontal) suppressClickUntil = performance.now() + 500;
        reset();
        if (surface.hasPointerCapture(event.pointerId)) surface.releasePointerCapture(event.pointerId);
        if (advance) select(wanted + (dx < 0 ? 1 : -1));
      });
      listen(surface, 'pointercancel', () => reset());
      // Touch transfers implicit image capture to the stage; that child event bubbles.
      listen(surface, 'lostpointercapture', event => {
        if (event.target === surface) reset();
      });
      listen(surface, 'click', event => {
        if (event.detail > 0 && performance.now() < suppressClickUntil) {event.preventDefault(); event.stopPropagation();}
      }, {capture: true});
    });
    $$('[data-gallery-retry]').forEach(el => listen(el, 'click', () => {
      (dialog.open ? $('[data-gallery-close]') : thumbs[current]).focus({preventScroll: true});
      select(wanted, true);
    }));
    listen(image, 'error', () => {if (!mobileRail.active()) feedback(strings.error, true);});
    listen(large, 'error', () => {if (dialog.open) feedback(strings.error, true);});
    if (mobileRail.active()) watchMobileImage(current);
    else if (image.complete && !image.naturalWidth) feedback(strings.error, true);
    listen(root, 'shopify:block:select', event => {
      const el = event.target.closest('[data-gallery-src]');
      const index = thumbs.indexOf(el);
      if (index >= 0) select(index);
    });
    if (image.complete) warmNeighbors();
    else listen(image, 'load', warmNeighbors, {once: true});
    root.classList.add('gallery-ready');
    instances.set(root, () => {mobileRail.destroy(); thumbnailRail.destroy(); clearTimeout(warmTimer); previews.clear(); cancel(); if (dialog.open) dialog.close(); events.abort(); instances.delete(root);});
  }
  const mountWithin = parent => parent.querySelectorAll('[data-product-gallery]').forEach(mount);
  mountWithin(document);
  document.addEventListener('shopify:section:load', event => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-product-gallery]').forEach(root => instances.get(root)?.()));
})();
